/* =============================================================
   Site header
   Handles:
   - Mobile/tablet drawer toggle (hamburger ↔ close)
   - Desktop dropdowns: slide-in / slide-out, closing on outside click and Escape
   - "Shop" category tabs and animated drawer accordions (one open at a time)
   - Closing the drawer on Escape and on viewport resize to desktop
   ============================================================= */
(function () {
  'use strict';

  var DESKTOP_MQ = window.matchMedia('(min-width: 80rem)');

  function init(root) {
    if (!root || root.dataset.headerInit === 'true') return;
    root.dataset.headerInit = 'true';

    var toggle = root.querySelector('[data-header-toggle]');
    var drawer = root.querySelector('[data-header-drawer]');

    function setOpen(open) {
      root.setAttribute('data-open', String(open));
      if (toggle) toggle.setAttribute('aria-expanded', String(open));
      if (drawer) {
        drawer.setAttribute('aria-hidden', String(!open));
        if (open) {
          drawer.removeAttribute('inert');
        } else {
          drawer.setAttribute('inert', '');
          // Reset accordion state so the next open starts with everything collapsed.
          drawer.querySelectorAll('details[data-drawer-item][open]').forEach(function (d) {
            d.open = false;
          });
        }
      }
      document.documentElement.classList.toggle('site-header-locked', open);
    }

    if (toggle && drawer) {
      toggle.addEventListener('click', function () {
        var open = root.getAttribute('data-open') === 'true';
        setOpen(!open);
      });
    }

    // "Shop (category tabs)" mega menu: hovering, clicking or arrowing onto a
    // category shows its panel (ARIA tabs pattern, vertical list).
    root.querySelectorAll('[data-shop-tabs]').forEach(function (tabs) {
      var buttons = Array.prototype.slice.call(tabs.querySelectorAll('[data-shop-tab]'));
      var panels = Array.prototype.slice.call(tabs.querySelectorAll('[data-shop-panel]'));

      function activate(index, focus) {
        buttons.forEach(function (btn, i) {
          var active = i === index;
          btn.setAttribute('aria-selected', String(active));
          btn.setAttribute('tabindex', active ? '0' : '-1');
          if (panels[i]) panels[i].hidden = !active;
        });
        if (focus && buttons[index]) buttons[index].focus();
      }

      buttons.forEach(function (btn, i) {
        btn.addEventListener('click', function () { activate(i); });
        btn.addEventListener('mouseenter', function () { activate(i); });
        btn.addEventListener('keydown', function (event) {
          var next = null;
          if (event.key === 'ArrowDown') next = (i + 1) % buttons.length;
          if (event.key === 'ArrowUp') next = (i - 1 + buttons.length) % buttons.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = buttons.length - 1;
          if (next === null) return;
          event.preventDefault();
          activate(next, true);
        });
      });
    });

    // Drawer accordions (Skincare, Supplements…): animate the panel height on
    // open and close instead of the native instant toggle, and keep a single
    // one open — opening an accordion closes whichever one was open.
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    // Must outlast the panel's height transition in header.css (300ms).
    var ACCORDION_MS = 350;
    // The menu's accordions. The account one in the drawer footer is a
    // separate control and stays independent.
    var accordions = drawer
      ? Array.prototype.slice.call(drawer.querySelectorAll('.site-header__drawer-list details[data-drawer-item]'))
      : [];

    function accordionPanel(details) {
      return details.querySelector('.site-header__drawer-category-panel');
    }

    function finishPanel(panel) {
      if (panel._onHeightEnd) {
        panel.removeEventListener('transitionend', panel._onHeightEnd);
        panel._onHeightEnd = null;
      }
      panel.classList.remove('is-animating');
      panel.style.removeProperty('height');
      panel.style.removeProperty('opacity');
    }

    function afterHeight(panel, callback) {
      panel._onHeightEnd = function (event) {
        if (event.target !== panel || event.propertyName !== 'height') return;
        callback();
      };
      panel.addEventListener('transitionend', panel._onHeightEnd);
    }

    function closeAccordion(details) {
      if (!details.open) return;
      var panel = accordionPanel(details);
      // No animated panel (plain submenu), no motion wanted, or caught halfway
      // through its own animation: close at once.
      if (!panel || reduceMotion.matches || panel.classList.contains('is-animating')) {
        if (panel) finishPanel(panel);
        details.open = false;
        return;
      }
      panel.style.height = panel.scrollHeight + 'px';
      panel.style.opacity = '1';
      panel.classList.add('is-animating');
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          panel.style.height = '0px';
          panel.style.opacity = '0';
        });
      });
      afterHeight(panel, function () {
        details.open = false;
        finishPanel(panel);
      });
    }

    function openAccordion(details) {
      var panel = accordionPanel(details);
      if (!panel || reduceMotion.matches) {
        details.open = true;
        return;
      }
      details.open = true;
      var height = panel.scrollHeight;
      panel.style.height = '0px';
      panel.style.opacity = '0';
      panel.classList.add('is-animating');
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          panel.style.height = height + 'px';
          panel.style.opacity = '1';
        });
      });
      afterHeight(panel, function () { finishPanel(panel); });
    }

    function closeOtherAccordions(current) {
      accordions.forEach(function (other) {
        if (other !== current) closeAccordion(other);
      });
    }

    // An accordion collapsing above the one just tapped would carry it up and
    // away from the finger. While the panels move, the drawer scrolls by the
    // same amount so the tapped row stays where it was (as far as the scroll
    // range allows).
    function holdInPlace(summary) {
      var top = summary.getBoundingClientRect().top;
      var start = null;
      function step(now) {
        if (start === null) start = now;
        var shift = summary.getBoundingClientRect().top - top;
        if (shift) drawer.scrollTop += shift;
        if (now - start < ACCORDION_MS) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    }

    accordions.forEach(function (details) {
      var summary = details.querySelector('summary');
      var panel = accordionPanel(details);
      if (!summary) return;

      // Plain submenus and "reduce motion" keep the native toggle; this is
      // where they close the others. For the animated path it is a no-op: the
      // others are already closing by the time `toggle` fires.
      details.addEventListener('toggle', function () {
        if (!details.open) return;
        if (panel && panel.classList.contains('is-animating')) return;
        holdInPlace(summary);
        closeOtherAccordions(details);
      });

      if (!panel) return;

      summary.addEventListener('click', function (event) {
        if (reduceMotion.matches) return; // native toggle
        event.preventDefault();
        if (panel.classList.contains('is-animating')) return;

        if (details.open) {
          closeAccordion(details);
        } else {
          holdInPlace(summary);
          closeOtherAccordions(details);
          openAccordion(details);
        }
      });
    });

    // Desktop dropdowns slide in from under the bar and slide back out,
    // instead of the native instant <details> toggle. Switching straight from
    // one dropdown to another cross-fades without the slide.
    function dropdownPanel(details) {
      return details.querySelector(':scope > .site-header__megamenu, :scope > .site-header__simple-menu');
    }

    function afterTransition(panel, callback) {
      var done = false;
      function end(event) {
        if (event && event.target !== panel) return;
        if (done) return;
        done = true;
        panel.removeEventListener('transitionend', end);
        callback();
      }
      panel.addEventListener('transitionend', end);
      setTimeout(end, 450); // fallback if no transition runs
    }

    function closeDropdown(details, animate) {
      var panel = dropdownPanel(details);
      if (!details.open) return;
      if (!animate || !panel || reduceMotion.matches) {
        details.open = false;
        if (panel) panel.classList.remove('is-hidden-state', 'is-fading');
        details.classList.remove('is-closing');
        return;
      }
      details.classList.add('is-closing');
      panel.classList.add('is-hidden-state');
      afterTransition(panel, function () {
        if (!details.classList.contains('is-closing')) return; // reopened meanwhile
        details.classList.remove('is-closing');
        details.open = false;
        panel.classList.remove('is-hidden-state');
      });
    }

    function openDropdown(details) {
      var panel = dropdownPanel(details);
      var switching = false;
      root.querySelectorAll('details[data-dropdown][open]').forEach(function (other) {
        if (other !== details) {
          switching = true;
          closeDropdown(other, false);
        }
      });
      details.classList.remove('is-closing');
      if (!panel || reduceMotion.matches) {
        details.open = true;
        return;
      }
      // Snap to the hidden state with transitions off: the panel still carries
      // its last visible style, so transitioning *into* the hidden state and
      // straight back would cancel out and the panel would just pop in.
      panel.classList.add('is-no-transition', 'is-hidden-state');
      panel.classList.toggle('is-fading', switching);
      details.open = true;
      void panel.offsetHeight; // flush the hidden state
      panel.classList.remove('is-no-transition');
      requestAnimationFrame(function () {
        panel.classList.remove('is-hidden-state');
        afterTransition(panel, function () { panel.classList.remove('is-fading'); });
      });
    }

    root.querySelectorAll('details[data-dropdown]').forEach(function (details) {
      var summary = details.querySelector(':scope > summary');
      if (!summary) return;
      summary.addEventListener('click', function (event) {
        event.preventDefault();
        if (details.open && !details.classList.contains('is-closing')) closeDropdown(details, true);
        else openDropdown(details);
      });
    });

    // Close any open desktop dropdown on outside click
    document.addEventListener('click', function (event) {
      var dropdowns = root.querySelectorAll('details[data-dropdown]');
      dropdowns.forEach(function (d) {
        if (d.open && !d.contains(event.target)) closeDropdown(d, true);
      });
    });

    // Escape closes drawer + dropdowns
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      root.querySelectorAll('details[data-dropdown]').forEach(function (d) {
        closeDropdown(d, true);
      });
      if (root.getAttribute('data-open') === 'true') setOpen(false);
    });

    // Close drawer when crossing into desktop layout
    var onChange = function (e) {
      if (e.matches && root.getAttribute('data-open') === 'true') setOpen(false);
    };
    if (DESKTOP_MQ.addEventListener) DESKTOP_MQ.addEventListener('change', onChange);
    else if (DESKTOP_MQ.addListener) DESKTOP_MQ.addListener(onChange);

    // Keep the cart-count bubble in sync with the live cart after AJAX changes
    document.addEventListener('cart:updated', function () {
      refreshCartCount(root);
    });
  }

  function refreshCartCount(root) {
    fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart.js', {
      credentials: 'same-origin'
    })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) {
        if (!cart) return;
        var count = cart.item_count || 0;
        var bubble = root.querySelector('[data-header-cart-count]');
        var sr = root.querySelector('[data-header-cart-count-sr]');
        if (bubble) {
          bubble.textContent = count;
          if (count > 0) bubble.removeAttribute('hidden');
          else bubble.setAttribute('hidden', '');
        }
        if (sr) sr.textContent = count;
      })
      .catch(function () { /* no-op */ });
  }

  function start() {
    document.querySelectorAll('[data-header]').forEach(init);
  }

  if (document.readyState !== 'loading') start();
  else document.addEventListener('DOMContentLoaded', start);

  // Re-init on Theme Editor section reload
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector
      ? event.target.querySelector('[data-header]')
      : null;
    if (root) init(root);
  });
})();
